import React, { useState } from "react";
import {
  Modal,
  Button,
  Input,
  Upload,
  Alert,
  Tag,
  Typography,
  Space,
  notification,
} from "antd";
import { postReq } from "../api";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * "Fill with AI" — reads an uploaded resume (or pasted resume text) and
 * pre-fills the Add Candidate form.
 *
 * Backed by POST /{company|common}/candidate/ai-parse, which is READ-ONLY: it
 * creates nothing and stores nothing. This component only calls
 * form.setFieldsValue(), so the recruiter still reviews every field and submits
 * through the normal Submit button. Nothing about saving a candidate changes.
 *
 * Sibling of AiParseRequirement.jsx — same shape, same guarantees, different
 * form. Kept as a separate component rather than a generalised one because the
 * two forms disagree on almost every field, and the value of these components
 * is precisely in the per-field mapping notes below.
 */

const ACCEPTED = ".pdf,.docx,.txt,.md";

/**
 * The Add Candidate inputs carry maxLength attributes. Those limit TYPING only
 * — setFieldsValue is unaffected and there are no length validation rules, so a
 * longer value sets and submits fine. Overlong values are therefore left INTACT
 * (truncating someone's email address helps nobody) and flagged, because the
 * user cannot lengthen the field by hand if they edit it afterwards.
 */
const MAXLENGTHS = {
  name: 40,
  role: 40,
  email: 40,
  linkedin: 100,
  location: 40,
  education: 100,
};

const LABELS = {
  name: "Name",
  role: "Role",
  email: "Email",
  mobile: "Phone",
  linkedin: "Linkedin",
  location: "Location",
  education: "Education",
  technologyNiche: "Select Technology",
  description: "Description",
  exp: "Total Exp",
  relevantExp: "Relevant Exp",
  budget: "Monthly Budget",
  status: "Select Status",
};

/**
 * Maps the API response onto the antd form's field types.
 *
 * Three real mismatches are handled here; without them a field looks filled and
 * then fails validation on submit:
 *
 *  1. `mobile` is an InputNumber. The API returns a 10-digit STRING (it has to
 *     — the parser works in digits and a leading zero is meaningful to it), so
 *     it is converted with Number(). A number that would lose a leading zero is
 *     already flagged by the backend.
 *  2. `technologyNiche` values must be the exact strings the Select's options
 *     use. The API stringifies its ObjectIds and the options are `${data._id}`,
 *     so they line up — but the Select is only populated with the first 100
 *     tags, so any id that has no option is dropped rather than rendered as a
 *     raw ObjectId in the field.
 *  3. `status` uses the form's own vocabulary (`available` / `on_project` /
 *     `not_available`) which differs in case from the Mongoose enum. The value
 *     comes from the backend already in the form's spelling; it is passed
 *     through untouched so the two never have to agree in two places.
 */
const toFormValues = (fields, availableNicheIds) => {
  const num = (v) => {
    if (v === null || v === undefined || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const values = {};
  const flags = [];

  for (const key of ["name", "role", "email", "linkedin", "location", "education"]) {
    const v = fields[key];
    if (!v) continue;
    values[key] = v;
    const cap = MAXLENGTHS[key];
    if (cap && String(v).length > cap) {
      flags.push(
        `${LABELS[key]} is ${String(v).length} characters — longer than this field normally allows (${cap}). It will still save, but shorten it if you want to edit it by hand.`,
      );
    }
  }

  if (fields.description) values.description = fields.description;
  if (fields.status) values.status = fields.status;

  const mobile = num(fields.mobile);
  if (mobile !== undefined) values.mobile = mobile;

  for (const key of ["exp", "relevantExp", "budget"]) {
    const n = num(fields[key]);
    if (n !== undefined) values[key] = n;
  }

  if (Array.isArray(fields.technologyNiche) && fields.technologyNiche.length) {
    const ids = fields.technologyNiche.map(String);
    const usable = availableNicheIds.length
      ? ids.filter((id) => availableNicheIds.includes(id))
      : ids;
    if (usable.length) values.technologyNiche = usable;
    if (usable.length < ids.length) {
      flags.push(
        `${ids.length - usable.length} technology tag(s) matched but are not in this dropdown's list — pick them manually if you need them.`,
      );
    }
  }

  return { values, flags };
};

// Fields the agent reports on that have no input on this form. Never listed as
// "missing" — telling someone to fill a field that isn't on screen is just
// confusing. (Resume is handled separately: it IS on the form, and the attached
// file is put there automatically.)
const NOT_ON_FORM = new Set([]);

export default function AiParseCandidate({
  form,
  userRole,
  permissions = [],
  nicheOptions = [],
  onResumeFile,
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Mirrors the endpoint choice in candidate.js handleSubmit — a company login
  // and a permissioned user hit different route trees.
  const endpoint =
    userRole === "company"
      ? "/company/candidate/ai-parse"
      : "/common/candidate/ai-parse";

  const availableNicheIds = (nicheOptions || []).map((o) => String(o._id));

  const reset = () => {
    setText("");
    setFile(null);
    setResult(null);
    setLoading(false);
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const handleParse = async () => {
    if (!text.trim() && !file) {
      notification.warning({
        message: "Nothing to read",
        description: "Attach the resume, or paste its text.",
        placement: "topRight",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      let res;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        if (text.trim()) fd.append("text", text);
        // Content-Type is left unset on purpose so axios adds the multipart
        // boundary itself; setting it by hand produces a malformed request.
        res = await postReq(endpoint, fd);
      } else {
        res = await postReq(endpoint, { text });
      }

      if (!res || res.status !== 1) {
        notification.error({
          message: "Could not read the resume",
          description: res?.message || "Please try again.",
          placement: "topRight",
        });
        return;
      }

      const data = res.data;

      if (!data.isResume) {
        notification.warning({
          message: "This doesn't look like a resume",
          description:
            (data.reason || "") +
            " — you can still edit the text and try again, or fill the form manually.",
          placement: "topRight",
        });
        return;
      }

      const { values, flags } = toFormValues(data.fields, availableNicheIds);
      form.setFieldsValue(values);

      // The recruiter uploaded the resume to be read; the form needs a resume
      // file to save. Re-using the same file saves them uploading it twice, and
      // it is the file they chose — not something the agent produced. They can
      // still Replace it on the form.
      let resumeAttached = false;
      if (file && typeof onResumeFile === "function") {
        onResumeFile(file);
        resumeAttached = true;
      }

      setResult({
        filled: Object.keys(values),
        missing: (data.missingFields || []).filter((f) => !NOT_ON_FORM.has(f)),
        unmatched: data.unmatchedTerms || [],
        niche: data.resolved?.niche || [],
        notes: data.notes || [],
        confidence: data.fieldConfidence || {},
        source: data.meta?.source,
        flags,
        resumeAttached,
      });

      notification.success({
        message: `Filled ${Object.keys(values).length} field(s)`,
        description: "Check the values below, then Submit as usual.",
        placement: "topRight",
      });
    } catch (err) {
      notification.error({
        message: "Could not reach the AI service",
        description:
          err?.message || "Please try again, or fill the form manually.",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  const lowConfidence = result
    ? result.filled.filter((f) => (result.confidence[f] ?? 1) < 0.7)
    : [];

  return (
    <>
      <Button
        type="dashed"
        onClick={() => setOpen(true)}
        style={{ borderColor: "#3699FF", color: "#3699FF" }}
      >
        ✨ Fill with AI
      </Button>

      <Modal
        title={
          <span style={{ color: "#13487D", fontWeight: 700 }}>
            Fill the form from a resume
          </span>
        }
        open={open}
        onCancel={close}
        width={680}
        footer={[
          <Button key="close" onClick={close}>
            Close
          </Button>,
          <Button
            key="parse"
            type="primary"
            loading={loading}
            onClick={handleParse}
            style={{ background: "#3699FF", borderColor: "#3699FF" }}
          >
            {result ? "Read again" : "Read & fill form"}
          </Button>,
        ]}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Attach the candidate's resume — PDF or DOCX, however it was sent. The
          file is read for this preview only; nothing is saved until you press
          Submit on the form.
        </Text>

        <Space style={{ marginTop: 12 }} wrap>
          <Upload
            accept={ACCEPTED}
            maxCount={1}
            beforeUpload={(f) => {
              setFile(f);
              return false; // keep it client-side; we send it ourselves
            }}
            onRemove={() => setFile(null)}
            fileList={file ? [file] : []}
            disabled={loading}
          >
            <Button type="primary" ghost>
              Attach resume
            </Button>
          </Upload>
          <Text type="secondary" style={{ fontSize: 12 }}>
            PDF, DOCX, TXT — up to 10MB
          </Text>
        </Space>

        <div style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Or paste the resume text instead — and add anything the CV does not
            say (expected CTC, notice period) here either way.
          </Text>
          <TextArea
            rows={7}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              "e.g.\n\nExpected CTC 18 LPA, 30 days notice, open to Pune or remote.\n\n(or paste the whole resume here if you don't have the file)"
            }
            style={{ marginTop: 8 }}
            disabled={loading}
          />
        </div>

        {result && (
          <div style={{ marginTop: 16 }}>
            <Alert
              type="success"
              showIcon
              message={`Filled ${result.filled.length} field(s) on the form`}
              description={
                <div style={{ fontSize: 12 }}>
                  {result.source && (
                    <div style={{ marginBottom: 6 }}>
                      Read from: <strong>{result.source}</strong>
                    </div>
                  )}
                  {result.resumeAttached && (
                    <div style={{ marginBottom: 6 }}>
                      The same file has been attached as the candidate's{" "}
                      <strong>Resume</strong> — replace it on the form if that
                      isn't what you want.
                    </div>
                  )}
                  {result.niche.length > 0 && (
                    <div style={{ marginBottom: 6 }}>
                      Technology:{" "}
                      {result.niche.map((n) => (
                        <Tag key={n._id} color="blue">
                          {n.label}
                        </Tag>
                      ))}
                    </div>
                  )}
                </div>
              }
            />

            {lowConfidence.length > 0 && (
              <Alert
                style={{ marginTop: 10 }}
                type="warning"
                showIcon
                message="Double-check these — the AI wasn't confident"
                description={
                  <span style={{ fontSize: 12 }}>
                    {lowConfidence.map((f) => LABELS[f] || f).join(", ")}
                  </span>
                }
              />
            )}

            {result.missing.length > 0 && (
              <Alert
                style={{ marginTop: 10 }}
                type="info"
                showIcon
                message="Not on the resume — please fill these in"
                description={
                  <span style={{ fontSize: 12 }}>
                    {result.missing.map((f) => LABELS[f] || f).join(", ")}
                  </span>
                }
              />
            )}

            {result.unmatched.length > 0 && (
              <Alert
                style={{ marginTop: 10 }}
                type="info"
                showIcon
                message="Skills with no matching tag"
                description={
                  <span style={{ fontSize: 12 }}>
                    {result.unmatched.slice(0, 25).join(", ")} — add them as tags
                    first if you want them on this candidate.
                  </span>
                }
              />
            )}

            {(result.notes.length > 0 || result.flags.length > 0) && (
              <Alert
                style={{ marginTop: 10 }}
                type="warning"
                showIcon
                message="Notes"
                description={
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                    {result.notes.map((n, i) => (
                      <li key={`n${i}`}>{n}</li>
                    ))}
                    {result.flags.map((c, i) => (
                      <li key={`f${i}`}>{c}</li>
                    ))}
                  </ul>
                }
              />
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
